/**
 * useOverlayPersistence - Server persistence and sync for overlays
 * Elvis Aloha Collection - Arena Map Editor
 * 
 * Handles saving to and loading from the backend, with optimistic updates
 * and audit trail integration.
 */

import { useState, useCallback, useRef } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const useOverlayPersistence = (chartId) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  
  // Track pending changes for optimistic updates
  const pendingChangesRef = useRef(new Map());
  
  /**
   * Load overlays from server
   */
  const loadOverlays = useCallback(async () => {
    if (!chartId) return { overlays: [], error: 'No chart ID' };
    
    // Owner-only endpoint: skip entirely until authenticated (prevents first-mount 401)
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('ownerToken') : null;
    if (!token) return { overlays: [], error: null };
    
    setIsLoading(true);
    setLoadError(null);
    
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/overlays/${chartId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (!res.ok) {
        throw new Error(`Failed to load overlays: ${res.status}`);
      }
      
      const data = await res.json();
      const overlays = (data.overlays || []).map(o => ({
        ...o,
        chartId // Ensure chartId is set
      }));
      
      return { overlays, error: null };
    } catch (err) {
      const errorMsg = err.message || 'Failed to load overlays';
      setLoadError(errorMsg);
      return { overlays: [], error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [chartId]);
  
  /**
   * Save all overlays to server
   */
  const saveOverlays = useCallback(async (overlays) => {
    if (!chartId) return { success: false, error: 'No chart ID' };
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/overlays`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          chartId,
          overlays: overlays.filter(o => o.chartId === chartId)
        })
      });
      
      if (!res.ok) {
        throw new Error(`Failed to save overlays: ${res.status}`);
      }
      
      const data = await res.json();
      setLastSaveTime(new Date());
      
      return { success: true, data };
    } catch (err) {
      const errorMsg = err.message || 'Failed to save overlays';
      setSaveError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsSaving(false);
    }
  }, [chartId]);
  
  /**
   * Update publication status for a single overlay
   */
  const updateOverlayStatus = useCallback(async (overlayId, newStatus, reason = null) => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/overlays/${overlayId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus, reason })
      });
      
      if (!res.ok) {
        throw new Error(`Failed to update status: ${res.status}`);
      }
      
      const data = await res.json();
      return { success: true, data };
    } catch (err) {
      const errorMsg = err.message || 'Failed to update status';
      setSaveError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsSaving(false);
    }
  }, []);
  
  /**
   * Delete an overlay from server.
   * A 404 is treated as success (already gone - idempotent from the caller's
   * point of view). A 409 (e.g. published/archived governance block) surfaces
   * the server's explanation so the caller can show it and keep the object.
   */
  const deleteOverlay = useCallback(async (overlayId) => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/overlays/${overlayId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.status === 404) {
        return { success: true };
      }
      
      if (!res.ok) {
        let detail = `Failed to delete overlay: ${res.status}`;
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch (_) { /* non-JSON error body */ }
        throw new Error(detail);
      }
      
      return { success: true };
    } catch (err) {
      const errorMsg = err.message || 'Failed to delete overlay';
      setSaveError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsSaving(false);
    }
  }, []);
  
  /**
   * Create an audit entry for a change
   */
  const createAuditEntry = useCallback(async (entry) => {
    // Audit entries are created server-side when saving
    // This is a placeholder for future direct audit logging
    console.log('[Audit]', entry);
  }, []);
  
  /**
   * Clear errors
   */
  const clearErrors = useCallback(() => {
    setSaveError(null);
    setLoadError(null);
  }, []);
  
  return {
    // State
    isSaving,
    isLoading,
    lastSaveTime,
    saveError,
    loadError,
    
    // Operations
    loadOverlays,
    saveOverlays,
    updateOverlayStatus,
    deleteOverlay,
    createAuditEntry,
    clearErrors
  };
};

export default useOverlayPersistence;
